import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import type { Account } from '@accounting/shared';
import { useCompanies } from '../../hooks/useCompanies';
import { useAccounts } from '../../hooks/useAccounts';
import { useBankAccounts } from '../../hooks/useBank';
import {
  useItemUpdateLinkToken,
  useLinkPlaidAccountToCompany,
  useMePlaidItems,
  useRemapPlaidMapping,
  useRemovePlaidItem,
  useRotatePlaidItemToken,
  useSyncAllPlaid,
  useUnlinkPlaidMapping,
  useUpgradeManualToPlaid,
  type GlobalPlaidAccount,
  type GlobalPlaidItem,
} from '../../hooks/useMePlaid';
import { AccountPicker } from '../../components/accounts/AccountPicker';
import { ApiError } from '../../lib/api';
import { humanizeLabel } from '../../lib/formatters';

export const Route = createFileRoute('/_auth/plaid')({ component: PlaidAccountsPage });

// OAuth banks bounce the whole page out to the bank and back to /plaid with
// ?oauth_state_id=... We stash the update-mode link_token here so the resume
// handler can reopen Link with the SAME token and finish account selection.
const PLAID_UPDATE_RESUME_KEY = 'plaid_update_resume';

function PlaidAccountsPage() {
  const { data: items, isLoading, error, refetch } = useMePlaidItems();
  const { data: companies = [] } = useCompanies();
  const syncAll = useSyncAllPlaid();
  const returningFromOAuth =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).has('oauth_state_id');

  if (returningFromOAuth) {
    // Whatever the update was for (a reconnect after new MFA, or granting more
    // accounts), the Item is usable again: pull every connection so the feeds
    // catch up without a per-company Sync click.
    return (
      <PlaidUpdateResume
        onDone={() => syncAll.mutate(undefined, { onSettled: () => void refetch() })}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="card" style={{ padding: 24 }}>
        <p className="muted">Loading Plaid accounts…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card" style={{ padding: 24 }}>
        <p className="field-error">Could not load Plaid Items.</p>
        <button type="button" className="btn-secondary" onClick={() => void refetch()}>
          Try again
        </button>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 24, margin: 0 }}>Plaid accounts</h1>
          <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>
            Every bank account behind every Plaid connection you have, across all your companies.
            Pick which company and GL account each one feeds into.
          </p>
        </div>
        <button type="button" className="btn-secondary" onClick={() => void refetch()}>
          Refresh
        </button>
      </div>

      {items && items.length === 0 ? (
        <div className="card" style={{ padding: 24, textAlign: 'center' }}>
          <p className="muted" style={{ marginTop: 0 }}>
            No Plaid connections yet. Open a company, go to Bank, and click "Connect bank" to create one.
          </p>
        </div>
      ) : (
        items?.map((item) => <PlaidItemCard key={item.plaidItemId} item={item} companies={companies} />)
      )}
    </div>
  );
}

function PlaidItemCard({
  item,
  companies,
}: {
  item: GlobalPlaidItem;
  companies: { id: string; name: string }[];
}) {
  return (
    <div className="card" style={{ padding: 16, marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8, gap: 12 }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>{item.institutionName ?? '(unknown bank)'}</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className="muted" style={{ fontSize: 12, fontFamily: 'ui-monospace, monospace' }}>
            Item {item.plaidItemId.slice(0, 12)}…
          </span>
          <PlaidItemActions item={item} />
        </div>
      </div>
      {item.needsLogin ? (
        <p className="field-error" style={{ marginTop: 0, fontSize: 13 }}>
          The bank wants you to sign in again (new MFA, changed password). Click <strong>Reconnect</strong>:
          it reopens this same connection, you complete the bank's login and MFA, and every company on
          it syncs straight after. Do not create a new connection — that would duplicate the accounts
          and lose the sync position.
          <span className="muted" style={{ display: 'block', marginTop: 4 }}>{item.unreachableReason}</span>
        </p>
      ) : item.unreachable ? (
        <p className="field-error" style={{ marginTop: 0, fontSize: 13 }}>
          Could not reach Plaid for this Item: {item.unreachableReason ?? 'unknown'}. Showing only
          accounts already in our DB. You may need to re-connect this bank.
        </p>
      ) : null}
      <table className="table" style={{ marginTop: 8 }}>
        <thead>
          <tr>
            <th>Account</th>
            <th>Type</th>
            <th>Mapping</th>
            <th style={{ width: 460 }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {item.accounts.map((acct) => (
            <PlaidAccountRow
              key={acct.plaidAccountId}
              plaidItemId={item.plaidItemId}
              acct={acct}
              companies={companies}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Item-level actions: add accounts the bank didn't share at first link, and
 * remove the whole connection.
 */
function PlaidItemActions({ item }: { item: GlobalPlaidItem }) {
  const [confirming, setConfirming] = useState(false);
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const openedRef = useRef(false);

  const createUpdateToken = useItemUpdateLinkToken();
  const removeItem = useRemovePlaidItem();
  const rotateToken = useRotatePlaidItemToken();
  const syncAll = useSyncAllPlaid();

  const onSuccess = useCallback(() => {
    // Update mode issues no new access_token — the Item already exists. The
    // existing token is simply valid again, so pull every connection now and
    // re-read the Item (newly granted accounts show up as "Not linked").
    sessionStorage.removeItem(PLAID_UPDATE_RESUME_KEY);
    setLinkToken(null);
    setNotice('Signed in. Syncing every account on this connection…');
    syncAll.mutate(undefined, {
      onSuccess: (r) =>
        setNotice(
          `Reconnected. ${r.added} added, ${r.modified} updated across ${r.rowsTouched} account(s)` +
            (r.itemsFailed > 0 ? ` — ${r.itemsFailed} connection(s) still failing.` : '.'),
        ),
      onError: (e) => setErr(e instanceof ApiError ? e.message : 'Signed in, but the sync failed. Use Sync on the Bank page.'),
    });
  }, [syncAll]);

  const onExit = useCallback((exitErr: unknown) => {
    sessionStorage.removeItem(PLAID_UPDATE_RESUME_KEY);
    setLinkToken(null);
    if (exitErr) {
      const e = exitErr as { error_code?: string; display_message?: string; error_message?: string };
      setErr(
        [e.error_code, e.display_message ?? e.error_message].filter(Boolean).join(' · ') ||
          'Plaid Link closed unexpectedly.',
      );
    }
  }, []);

  const { open, ready } = usePlaidLink({ token: linkToken, onSuccess, onExit });

  useEffect(() => {
    if (linkToken && ready && !openedRef.current) {
      openedRef.current = true;
      open();
    }
  }, [linkToken, ready, open]);

  const startAddAccounts = () => {
    openedRef.current = false;
    setErr(null);
    setNotice(null);
    createUpdateToken.mutate(item.plaidItemId, {
      onSuccess: (res) => {
        // Stash for the OAuth round trip — major banks redirect the page away.
        sessionStorage.setItem(
          PLAID_UPDATE_RESUME_KEY,
          JSON.stringify({ token: res.linkToken, savedAt: Date.now() }),
        );
        setLinkToken(res.linkToken);
      },
      onError: (e) =>
        setErr(e instanceof ApiError ? e.message : 'Could not start the account-selection flow.'),
    });
  };

  const confirmRemove = () => {
    removeItem.mutate(item.plaidItemId, {
      onSuccess: (res) => {
        setConfirming(false);
        setNotice(
          res.removedAtPlaid
            ? `Connection removed at Plaid. ${res.rowsDeactivated} bank row(s) retired. Billing for this Item has ended.`
            : (res.warning ?? 'Connection retired locally.'),
        );
      },
      onError: (e) =>
        setErr(e instanceof ApiError ? e.message : 'Could not remove this connection.'),
    });
  };

  const busy = createUpdateToken.isPending || syncAll.isPending || (!!linkToken && !ready);

  return (
    <>
      {item.needsLogin && (
        <button
          type="button"
          className="btn-primary"
          onClick={startAddAccounts}
          disabled={busy}
          title="Sign in to the bank again (MFA included) on this same connection, then sync every company on it"
        >
          {busy ? 'Working…' : 'Reconnect'}
        </button>
      )}
      <button
        type="button"
        className="btn-secondary"
        onClick={startAddAccounts}
        disabled={busy || item.unreachable}
        title="Re-open the bank's account picker to share accounts that weren't available the first time"
      >
        {createUpdateToken.isPending && !item.needsLogin ? 'Starting…' : 'Add accounts'}
      </button>
      <button
        type="button"
        className="btn-secondary"
        onClick={() => {
          setErr(null);
          setNotice(null);
          rotateToken.mutate(item.plaidItemId, {
            onSuccess: (res) =>
              setNotice(
                `Credential rotated. ${res.rowsUpdated} row(s) re-encrypted; the old token is dead. No re-login needed.`,
              ),
            onError: (e) =>
              setErr(e instanceof ApiError ? e.message : 'Could not rotate the credential.'),
          });
        }}
        disabled={rotateToken.isPending || item.unreachable}
        title="Issue a new Plaid access token and invalidate the old one — no bank re-login"
      >
        {rotateToken.isPending ? 'Rotating…' : 'Rotate credential'}
      </button>
      <button
        type="button"
        className="btn-danger"
        onClick={() => setConfirming(true)}
        disabled={removeItem.isPending}
      >
        Remove connection
      </button>

      {(notice || err) && (
        <span
          className={err ? 'field-error' : 'muted'}
          style={{ fontSize: 12, maxWidth: 340, display: 'inline-block' }}
        >
          {err ?? notice}
        </span>
      )}

      {confirming && (
        <RemoveConnectionModal
          item={item}
          pending={removeItem.isPending}
          onCancel={() => setConfirming(false)}
          onConfirm={confirmRemove}
        />
      )}
    </>
  );
}

/**
 * Two-step "are you sure — no really" gate. Removing an Item cannot be undone
 * from inside the app: the access token is destroyed at Plaid and getting the
 * feed back means a fresh bank sign-in.
 */
function RemoveConnectionModal({
  item,
  pending,
  onCancel,
  onConfirm,
}: {
  item: GlobalPlaidItem;
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const [reallySure, setReallySure] = useState(false);
  const linkedCount = item.accounts.filter((a) => a.linkedBankAccountId).length;

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" onClick={onCancel}>
      <div className="modal" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        {!reallySure ? (
          <>
            <h3 style={{ marginTop: 0 }}>
              Remove the {item.institutionName ?? 'bank'} connection?
            </h3>
            <p style={{ fontSize: 14 }}>
              This deletes the connection at Plaid, which is what actually stops the monthly
              charge for this bank login. {linkedCount} linked bank row
              {linkedCount === 1 ? '' : 's'} will stop syncing.
            </p>
            <p style={{ fontSize: 14 }}>
              <strong>Your books are not touched.</strong> Every transaction, journal entry, match
              and reconciliation stays exactly as it is. Only the live feed goes away.
            </p>
            <p className="muted" style={{ fontSize: 13 }}>
              To get the feed back you'd have to sign in to the bank again from scratch.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button type="button" className="btn-secondary" onClick={onCancel}>
                Cancel
              </button>
              <button type="button" className="btn-danger" onClick={() => setReallySure(true)}>
                Yes, remove it
              </button>
            </div>
          </>
        ) : (
          <>
            <h3 style={{ marginTop: 0 }}>You really mean it? Like, for real?</h3>
            <p style={{ fontSize: 14 }}>
              Last chance. Removing <strong>{item.institutionName ?? 'this bank'}</strong> (Item{' '}
              <code>{item.plaidItemId.slice(0, 12)}…</code>) is permanent at Plaid and cannot be
              undone from this app.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button type="button" className="btn-secondary" onClick={onCancel} disabled={pending}>
                No, keep it
              </button>
              <button type="button" className="btn-danger" onClick={onConfirm} disabled={pending}>
                {pending ? 'Removing…' : 'For real — remove it'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Finishes account selection after an OAuth bank redirects back to /plaid.
 * Mirrors the resume handler on the Bank page, minus the token exchange —
 * update mode reuses the existing Item, so there is nothing to exchange.
 */
function PlaidUpdateResume({ onDone }: { onDone: () => void }) {
  const [done, setDone] = useState(false);
  const openedRef = useRef(false);
  const savedRaw =
    typeof window !== 'undefined' ? sessionStorage.getItem(PLAID_UPDATE_RESUME_KEY) : null;
  const saved = (() => {
    if (!savedRaw) return null;
    try {
      const parsed = JSON.parse(savedRaw) as { token: string; savedAt: number };
      // Plaid link_tokens live ~4 hours; anything older is not resumable.
      if (Date.now() - parsed.savedAt > 4 * 60 * 60 * 1000) return null;
      return parsed;
    } catch {
      return null;
    }
  })();

  const cleanup = useCallback(() => {
    sessionStorage.removeItem(PLAID_UPDATE_RESUME_KEY);
    window.history.replaceState({}, '', '/plaid');
    setDone(true);
    onDone();
  }, [onDone]);

  const { open, ready } = usePlaidLink({
    token: saved?.token ?? null,
    receivedRedirectUri: typeof window !== 'undefined' ? window.location.href : undefined,
    onSuccess: cleanup,
    onExit: cleanup,
  });

  useEffect(() => {
    if (!saved && !done) {
      cleanup();
      return;
    }
    if (saved && ready && !openedRef.current) {
      openedRef.current = true;
      open();
    }
  }, [saved, ready, open, done, cleanup]);

  if (done) return null;
  return (
    <div className="card" style={{ padding: 16 }}>
      <strong>Finishing account selection…</strong>
      <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>
        Returning from your bank's sign-in.
      </p>
    </div>
  );
}

function PlaidAccountRow({
  plaidItemId,
  acct,
  companies,
}: {
  plaidItemId: string;
  acct: GlobalPlaidAccount;
  companies: { id: string; name: string }[];
}) {
  const [mode, setMode] = useState<'view' | 'editing'>('view');
  const [companyId, setCompanyId] = useState<string>(acct.linkedCompanyId ?? '');
  const [glAccountId, setGlAccountId] = useState<string>(acct.linkedGlAccountId ?? '');
  const [opError, setOpError] = useState<string | null>(null);

  const link = useLinkPlaidAccountToCompany();
  const remap = useRemapPlaidMapping();
  const unlink = useUnlinkPlaidMapping();
  const upgrade = useUpgradeManualToPlaid();
  const isLinked = !!acct.linkedBankAccountId;

  // Pull this company's CoA only when a target is chosen.
  const { data: accounts = [] } = useAccounts(companyId || null);
  const bankAccountsForCompany = useBankAccounts(companyId || null);
  const manualRowsInCompany = (bankAccountsForCompany.data?.bankAccounts ?? []).filter(
    (b) => b.isActive && b.isManual && !b.isMock,
  );
  const [upgradeTargetId, setUpgradeTargetId] = useState<string>('');

  const setError = (err: unknown) =>
    setOpError(
      err instanceof ApiError
        ? (err.body as { error?: string } | null)?.error ?? 'Request failed'
        : err instanceof Error
          ? err.message
          : 'Request failed',
    );

  const handleLink = () => {
    if (!companyId || !glAccountId) return;
    setOpError(null);
    link.mutate(
      { plaidItemId, plaidAccountId: acct.plaidAccountId, companyId, glAccountId },
      { onSuccess: () => setMode('view'), onError: setError },
    );
  };

  const handleRemap = () => {
    if (!acct.linkedBankAccountId || !companyId || !glAccountId) return;
    setOpError(null);
    remap.mutate(
      { bankAccountId: acct.linkedBankAccountId, companyId, glAccountId },
      { onSuccess: () => setMode('view'), onError: setError },
    );
  };

  const handleUnlink = () => {
    if (!acct.linkedBankAccountId) return;
    if (!confirm('Unlink this Plaid account? Existing transactions stay; the row stops syncing.')) return;
    setOpError(null);
    unlink.mutate(acct.linkedBankAccountId, { onError: setError });
  };

  const handleUpgrade = () => {
    if (!upgradeTargetId) return;
    setOpError(null);
    upgrade.mutate(
      { manualBankAccountId: upgradeTargetId, plaidItemId, plaidAccountId: acct.plaidAccountId },
      {
        onSuccess: () => {
          setMode('view');
          setUpgradeTargetId('');
        },
        onError: setError,
      },
    );
  };

  return (
    <tr>
      <td>
        <div style={{ fontWeight: 500 }}>{acct.name}</div>
        <div className="muted" style={{ fontSize: 12 }}>
          {acct.mask ? `····${acct.mask}` : acct.plaidAccountId.slice(0, 12) + '…'}
        </div>
      </td>
      <td className="muted" style={{ fontSize: 13 }}>
        {[acct.type, acct.subtype].filter(Boolean).map(humanizeLabel).join(' / ') || '—'}
      </td>
      <td>
        {isLinked ? (
          <div>
            <div style={{ fontSize: 13 }}>
              <strong>{acct.linkedCompanyName ?? '(unknown)'}</strong>
            </div>
            <div className="muted" style={{ fontSize: 12 }}>
              {acct.linkedGlAccountCode ? `${acct.linkedGlAccountCode} · ` : ''}
              {acct.linkedGlAccountName ?? '(no GL)'}
            </div>
          </div>
        ) : (
          <span className="muted" style={{ fontSize: 12, fontStyle: 'italic' }}>Not linked</span>
        )}
      </td>
      <td>
        {mode === 'view' ? (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button
              type="button"
              className={isLinked ? 'btn-secondary' : 'btn-primary'}
              onClick={() => {
                setCompanyId(acct.linkedCompanyId ?? '');
                setGlAccountId(acct.linkedGlAccountId ?? '');
                setMode('editing');
              }}
            >
              {isLinked ? 'Remap' : 'Link to company'}
            </button>
            {isLinked && (
              <button
                type="button"
                className="btn-secondary"
                disabled={unlink.isPending}
                onClick={handleUnlink}
              >
                {unlink.isPending ? 'Unlinking…' : 'Unlink'}
              </button>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 460 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr', gap: 6, alignItems: 'center' }}>
              <label className="muted" style={{ fontSize: 12 }}>Company</label>
              <select
                value={companyId}
                onChange={(e) => {
                  setCompanyId(e.target.value);
                  setGlAccountId('');
                  setUpgradeTargetId('');
                }}
              >
                <option value="">— pick a company —</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>

              <label className="muted" style={{ fontSize: 12 }}>GL account</label>
              <AccountPicker
                id={`gl-${acct.plaidAccountId}`}
                accounts={accounts}
                value={glAccountId}
                onChange={setGlAccountId}
                predicate={(a: Account) =>
                  a.isActive &&
                  a.allowDirectPosting &&
                  (a.accountType === 'asset' || a.accountType === 'liability')
                }
                placeholder={!companyId ? '(pick a company first)' : '— pick GL —'}
              />

              {!isLinked && manualRowsInCompany.length > 0 && (
                <>
                  <label className="muted" style={{ fontSize: 12 }}>
                    Or upgrade a manual row
                    <div style={{ fontSize: 10, marginTop: 2 }}>(keeps its existing txns)</div>
                  </label>
                  <select
                    value={upgradeTargetId}
                    onChange={(e) => setUpgradeTargetId(e.target.value)}
                  >
                    <option value="">— leave manual rows alone —</option>
                    {manualRowsInCompany.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.accountName}{b.accountMask ? ` ····${b.accountMask}` : ''}
                      </option>
                    ))}
                  </select>
                </>
              )}
            </div>

            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {!isLinked && upgradeTargetId ? (
                <button
                  type="button"
                  className="btn-primary"
                  disabled={upgrade.isPending}
                  onClick={handleUpgrade}
                  title="Upgrade the selected manual placeholder to use this Plaid feed."
                >
                  {upgrade.isPending ? 'Upgrading…' : 'Upgrade manual → Plaid'}
                </button>
              ) : !isLinked ? (
                <button
                  type="button"
                  className="btn-primary"
                  disabled={!companyId || !glAccountId || link.isPending}
                  onClick={handleLink}
                >
                  {link.isPending ? 'Linking…' : 'Create new linked row'}
                </button>
              ) : (
                <button
                  type="button"
                  className="btn-primary"
                  disabled={!companyId || !glAccountId || remap.isPending}
                  onClick={handleRemap}
                >
                  {remap.isPending ? 'Saving…' : 'Save remap'}
                </button>
              )}
              <button type="button" className="btn-secondary" onClick={() => { setMode('view'); setOpError(null); }}>
                Cancel
              </button>
            </div>
            {opError && <div className="field-error" style={{ fontSize: 12 }}>{opError}</div>}
          </div>
        )}
      </td>
    </tr>
  );
}
